import { memo, ReactNode, useEffect } from 'react';
import { ConnectDragSource, useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import styled, { css } from 'styled-components';

import {
  ScreenEditorComponentItem,
  ScreenEditorDraggableItemTypes,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import hoverableMixin from 'design-system/mixins/hoverable';
import { UiComponentConfig } from 'lib/interfaces/ui-components';

type ComponentLibraryBlockProps = {
  icon: ReactNode;
  label: string;
  config: Omit<UiComponentConfig, 'name'>;
  cornerUI?: ReactNode;
  draggableSource?: ConnectDragSource;
  disabled?: boolean;
};

const ComponentDisplayContainer = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
  text-align: center;
  position: relative;

  ${({ disabled, theme }) =>
    disabled &&
    css`
      user-select: none;
      cursor: not-allowed;

      ${TileParagraph} {
        color: ${theme.color.text.disabled};
      }
    `}
`;

const IconContainer = styled.div`
  ${hoverableMixin}
  width: 72px;
  height: 72px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TileParagraph = styled(Paragraph).attrs({
  size: 'xs',
})`
  color: ${({ theme }) => theme.color.primary.black};
`;

const ComponentLibraryBlock = ({
  icon,
  label,
  config,
  cornerUI,
  disabled,
}: ComponentLibraryBlockProps) => {
  const [_, dragRef, dragPreview] = useDrag(() => ({
    type: ScreenEditorDraggableItemTypes.COMPONENT,
    item: { ...config } as ScreenEditorComponentItem,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !disabled,
  }));

  //disable default drag preview
  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  return (
    <ComponentDisplayContainer disabled={disabled}>
      {cornerUI && cornerUI}
      <IconContainer
        ref={dragRef}
        data-test={`component-library-block__${config.type}`}
        disabled={disabled}
      >
        {icon}
      </IconContainer>
      <Spacer height={1} />
      <TileParagraph>{label}</TileParagraph>
    </ComponentDisplayContainer>
  );
};

export default memo(ComponentLibraryBlock);
